import { PROTOCOL_VERSION, encodeFrame, parseServerFrame } from '../../shared/protocol.ts';
import type { ClientFrame, ServerFrame } from '../../shared/protocol.ts';

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface SocketEvents {
  frame(frame: ServerFrame): void;
  state(state: ConnectionState): void;
  /** A frame the server sent that this client cannot parse. Never silently dropped. */
  protocolError(reason: string): void;
}

export interface MatchSocketOptions {
  readonly url?: string;
  readonly minBackoffMs?: number;
  readonly maxBackoffMs?: number;
  /** Injected in tests. */
  readonly createSocket?: (url: string) => WebSocket;
  readonly random?: () => number;
}

const MIN_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 8000;

function defaultUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

/**
 * Transport only. It connects, reconnects, and hands typed frames upwards.
 *
 * It knows nothing about Connect 4 — no board, no seats, no rules. Reconnection
 * is not an optimisation here: Railway drops WebSockets on every deploy, so a
 * client that cannot resume is a client that loses matches.
 */
export class MatchSocket {
  #socket: WebSocket | null = null;
  #state: ConnectionState = 'closed';
  #attempt = 0;
  #retryTimer: ReturnType<typeof setTimeout> | null = null;
  #closedByUs = false;

  /** Frames queued while offline, flushed in order once `hello` is acknowledged. */
  readonly #outbox: ClientFrame[] = [];
  readonly #listeners: { [K in keyof SocketEvents]: Set<SocketEvents[K]> } = {
    frame: new Set(),
    state: new Set(),
    protocolError: new Set(),
  };

  readonly #options: Required<Omit<MatchSocketOptions, 'createSocket'>> &
    Pick<MatchSocketOptions, 'createSocket'>;

  /** What to resume into after a reconnect. Updated by the consumer. */
  #resume: { matchId: string; sinceSeq: number } | null = null;
  #greeted = false;

  constructor(options: MatchSocketOptions = {}) {
    this.#options = {
      url: options.url ?? defaultUrl(),
      minBackoffMs: options.minBackoffMs ?? MIN_BACKOFF_MS,
      maxBackoffMs: options.maxBackoffMs ?? MAX_BACKOFF_MS,
      random: options.random ?? Math.random,
      ...(options.createSocket ? { createSocket: options.createSocket } : {}),
    };
  }

  get state(): ConnectionState {
    return this.#state;
  }

  on<K extends keyof SocketEvents>(event: K, handler: SocketEvents[K]): () => void {
    this.#listeners[event].add(handler);
    return () => {
      this.#listeners[event].delete(handler);
    };
  }

  #emitFrame(frame: ServerFrame): void {
    for (const handler of this.#listeners.frame) handler(frame);
  }

  #setState(state: ConnectionState): void {
    if (this.#state === state) return;
    this.#state = state;
    for (const handler of this.#listeners.state) handler(state);
  }

  /** Tells the socket what to resume into after any future reconnect. */
  trackMatch(matchId: string, sinceSeq: number): void {
    this.#resume = { matchId, sinceSeq };
  }

  forgetMatch(): void {
    this.#resume = null;
  }

  connect(): void {
    this.#closedByUs = false;
    if (this.#socket && this.#socket.readyState <= WebSocket.OPEN) return;
    this.#open();
  }

  #open(): void {
    this.#greeted = false;
    this.#setState(this.#attempt === 0 ? 'connecting' : 'reconnecting');

    const socket = this.#options.createSocket
      ? this.#options.createSocket(this.#options.url)
      : new WebSocket(this.#options.url);
    this.#socket = socket;

    socket.addEventListener('open', () => {
      // `hello` always goes first; nothing queued may overtake it.
      socket.send(encodeFrame({ t: 'hello', protocolVersion: PROTOCOL_VERSION }));
    });

    socket.addEventListener('message', (event: MessageEvent) => {
      const raw = typeof event.data === 'string' ? event.data : '';
      const parsed = parseServerFrame(raw);
      if (!parsed.ok) {
        for (const handler of this.#listeners.protocolError) handler(parsed.reason);
        return;
      }

      if (parsed.frame.t === 'hello_ok') {
        this.#greeted = true;
        this.#attempt = 0;
        this.#setState('open');
        // Resume before flushing: queued moves are only meaningful inside a match.
        if (this.#resume) {
          socket.send(
            encodeFrame({
              t: 'resume',
              matchId: this.#resume.matchId,
              sinceSeq: this.#resume.sinceSeq,
            }),
          );
        }
        this.#flush();
      }

      this.#emitFrame(parsed.frame);
    });

    socket.addEventListener('close', () => {
      this.#socket = null;
      this.#greeted = false;
      if (this.#closedByUs) {
        this.#setState('closed');
        return;
      }
      this.#scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // `close` always follows; reconnection is handled there so it happens once.
    });
  }

  #scheduleReconnect(): void {
    this.#setState('reconnecting');
    const exponential = Math.min(
      this.#options.maxBackoffMs,
      this.#options.minBackoffMs * 2 ** this.#attempt,
    );
    // Full jitter: without it every client reconnects in lockstep after a deploy.
    const delay = this.#options.minBackoffMs + this.#options.random() * (exponential - this.#options.minBackoffMs);
    this.#attempt += 1;

    if (this.#retryTimer !== null) clearTimeout(this.#retryTimer);
    this.#retryTimer = setTimeout(() => this.#open(), delay);
  }

  #flush(): void {
    if (!this.#socket || this.#socket.readyState !== WebSocket.OPEN || !this.#greeted) return;
    while (this.#outbox.length > 0) {
      const frame = this.#outbox.shift();
      if (frame) this.#socket.send(encodeFrame(frame));
    }
  }

  /** Sends now if possible, otherwise queues until the next successful `hello`. */
  send(frame: ClientFrame): void {
    if (this.#socket?.readyState === WebSocket.OPEN && this.#greeted) {
      this.#socket.send(encodeFrame(frame));
      return;
    }
    this.#outbox.push(frame);
  }

  close(): void {
    this.#closedByUs = true;
    if (this.#retryTimer !== null) {
      clearTimeout(this.#retryTimer);
      this.#retryTimer = null;
    }
    this.#socket?.close();
    this.#socket = null;
    this.#setState('closed');
  }
}
